import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { useLanguage } from '../../hooks/useLanguage';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import TypingIndicator from './TypingIndicator';
import WelcomeMessage from './WelcomeMessage';
import GideonHeader from './GideonHeader';
import ProgressIndicator, { CompactProgressIndicator } from '../ProgressIndicator';
import useSSE from '../../hooks/useSSE';
import { useIsMobile } from '../../hooks/useIsMobile';
import { extractVisualizations } from '../../utils/extractVisualizations';

// LocalStorage keys
const ACTIVE_SESSION_KEY = 'gideon_active_session';

// Generate UUID v4 for session IDs
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const ChatInterface = ({
  conversationId,
  conversations = [],
  onToggleSidebar,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
  onConversationsUpdate,
  showMenuButton = false,
  initialPrompt = null,
  onInitialPromptUsed = null
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasCompletedOnboarding, startOnboardingFromChat } = useOnboarding();
  const { language } = useLanguage();

  // State
  const [messages, setMessages] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();

  // Session management state
  const [sessionId, setSessionId] = useState(conversationId);
  const [isLoadingSession, setIsLoadingSession] = useState(false);

  // SSE Progress state
  const [currentProgress, setCurrentProgress] = useState(null);
  const [sseSessionId, setSseSessionId] = useState(null);

  // Onboarding state
  const [showAssessmentPrompt, setShowAssessmentPrompt] = useState(false);
  const [onboardingContext, setOnboardingContext] = useState(null);

  // Nudging system state
  const [nudgeCount, setNudgeCount] = useState(0);
  const [lastNudgeTime, setLastNudgeTime] = useState(0);
  const MAX_NUDGES_PER_SESSION = 3;
  const NUDGE_COOLDOWN = 30000;

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastAiMessageRef = useRef(null);
  const currentRequest = useRef(null);
  const requestCache = useRef(new Map());
  const CACHE_DURATION = 5000;

  // Sync sessionId with conversationId prop
  useEffect(() => {
    if (conversationId !== sessionId) {
      setSessionId(conversationId);
    }
  }, [conversationId]);


  // Initialize with a new session if no conversationId provided
  useEffect(() => {
    if (!sessionId && !conversationId) {
      const newSessionId = generateUUID();
      console.log(`[CHAT] Starting new conversation: ${newSessionId}`);
      setSessionId(newSessionId);
      setMessages([{
        type: 'ai',
        content: "Hey there! I'm Gideon, your AI financial guide. Ask me about stocks, investments, market data, or how the financial world works!",
        timestamp: new Date()
      }]);
      localStorage.setItem(ACTIVE_SESSION_KEY, newSessionId);
    }
  }, []);

  // Load messages when sessionId changes
  useEffect(() => {
    if (!sessionId || isLoadingSession) return;

    const loadMessages = async () => {
      try {
        const token = localStorage.getItem('userToken');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        const response = await fetch(`${apiUrl}/api/agent/conversations/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const data = await response.json();

        if (data.success && data.data && data.data.messages) {
          console.log(`[CHAT] Loaded ${data.data.messages.length} messages`);

          // Transform messages from backend format to frontend format
          const transformedMessages = data.data.messages.map(msg => ({
            type: msg.role === 'user' ? 'user' : 'ai',
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            suggestions: msg.suggestions || [],
            hasVisualContent: msg.hasVisualContent,
            visualizations: msg.visualizations
          }));

          setMessages(transformedMessages);
          localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
        } else {
          console.warn(`[CHAT] Conversation ${sessionId} not found or empty`);
          // Start with welcome message for new conversation
          setMessages([{
            type: 'ai',
            content: "Hey there! I'm Gideon, your AI financial guide. Ask me about stocks, investments, market data, or how the financial world works!",
            timestamp: new Date()
          }]);
          localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
        }
      } catch (error) {
        console.error('[CHAT] Error loading conversation:', error);
        // Start with welcome message on error
        setMessages([{
          type: 'ai',
          content: "Hey there! I'm Gideon, your AI financial guide. Ask me about stocks, investments, market data, or how the financial world works!",
          timestamp: new Date()
        }]);
      }
    };

    loadMessages();
  }, [sessionId, isLoadingSession]);

  // Handle initial prompt from URL
  const initialPromptProcessed = useRef(false);
  useEffect(() => {
    if (initialPrompt && !initialPromptProcessed.current && messages.length > 0 && !isLoading) {
      initialPromptProcessed.current = true;
      // Small delay to ensure the welcome message is rendered first
      const timer = setTimeout(() => {
        handleSendMessage(initialPrompt);
        if (onInitialPromptUsed) {
          onInitialPromptUsed();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [initialPrompt, messages.length, isLoading]);

  // Scroll when messages change
  useEffect(() => {
    const timer = setTimeout(() => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.type === 'ai' && lastAiMessageRef.current) {
        lastAiMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        scrollToBottom();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [messages.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.abort();
      }
      requestCache.current.clear();
    };
  }, []);

  // Scroll functions
  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    }
  };

  // Request utilities
  const createRequestHash = (text) => {
    try {
      return btoa(text).slice(0, 16);
    } catch (e) {
      return text.substring(0, 16);
    }
  };

  const cancelCurrentRequest = () => {
    if (currentRequest.current) {
      currentRequest.current.abort();
      currentRequest.current = null;
    }
  };

  const getCachedResult = (questionHash) => {
    const cached = requestCache.current.get(questionHash);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.result;
    }
    return null;
  };

  // Nudging system
  const detectTradingIntent = (messageText) => {
    if (!messageText) return false;
    const tradingKeywords = ['buy', 'sell', 'invest', 'stock', 'portfolio', 'trade', 'trading', 'recommend'];
    const lowerMessage = messageText.toLowerCase();
    return tradingKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  const shouldShowNudge = (messageText) => {
    if (hasCompletedOnboarding) return false;
    if (nudgeCount >= MAX_NUDGES_PER_SESSION) return false;
    if (Date.now() - lastNudgeTime < NUDGE_COOLDOWN) return false;
    return detectTradingIntent(messageText) || Math.random() < 0.2;
  };

  // Detect suitability hints
  const detectSuitabilityHint = (responseData, originalQuestion = '') => {
    if (responseData.suitabilityHint && responseData.suitabilityHint.required && !hasCompletedOnboarding) {
      return {
        required: true,
        message: responseData.suitabilityHint.gideonPrompt || "Let's have a quick chat so I can give you personalized investment advice!",
        benefits: responseData.suitabilityHint.benefits || ['Get personalized recommendations', 'Learn at your pace'],
        originalQuestion
      };
    }
    return null;
  };

  // SSE Callbacks
  const handleSSEProgress = useCallback((event) => {
    // Replace "Claude" with "Gideon" in progress messages for branding
    const transformedEvent = {
      ...event,
      step: event.step?.replace(/Claude/gi, 'Gideon'),
      message: event.message?.replace(/Claude/gi, 'Gideon')
    };
    setCurrentProgress(transformedEvent);
  }, []);

  const handleSSEComplete = useCallback((data) => {
    const aiMessage = {
      type: 'ai',
      content: data.message,
      suggestions: data.suggestions || [],
      hasVisualContent: data.hasVisualContent,
      visualizations: extractVisualizations(data),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, aiMessage]);
    setIsLoading(false);
    setCurrentProgress(null);
    setSseSessionId(null);

    const suitabilityHint = detectSuitabilityHint(data, '');
    if (suitabilityHint) {
      setTimeout(() => setShowAssessmentPrompt(suitabilityHint), 1000);
    }
  }, [hasCompletedOnboarding, sessionId]);

  const handleSSEError = useCallback((error) => {
    setIsLoading(false);
    setCurrentProgress(null);
    setSseSessionId(null);

    setMessages(prev => [...prev, {
      type: 'ai',
      content: error.message || 'An error occurred while processing your request.',
      timestamp: new Date()
    }]);
  }, []);

  // Setup SSE hook
  const { isConnected } = useSSE(
    sseSessionId,
    handleSSEProgress,
    handleSSEComplete,
    handleSSEError,
    {
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      autoReconnect: true,
      maxReconnectAttempts: 3
    }
  );

  // Handle send message
  const handleSendMessage = useCallback(async (inputValue) => {
    if (!inputValue.trim() || isLoading) return;

    cancelCurrentRequest();

    // Check cache
    const questionHash = createRequestHash(inputValue);
    const cachedResult = getCachedResult(questionHash);
    if (cachedResult) {
      setMessages(prev => [...prev, {
        type: 'ai',
        content: cachedResult.message,
        suggestions: cachedResult.suggestions || [],
        timestamp: new Date()
      }]);
      return;
    }

    // Check token
    const token = localStorage.getItem('userToken');
    if (!token) {
      setMessages(prev => [...prev, {
        type: 'ai',
        content: "Please log in to use the AI assistant.",
        timestamp: new Date()
      }]);
      return;
    }

    // Add user message
    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/agent/ask/async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: inputValue,
          sessionId: sessionId,
          userLanguage: language
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.sessionId) {
        setSseSessionId(data.sessionId);
        setCurrentProgress({
          type: 'progress',
          step: 'Connecting...',
          message: 'Establishing connection to AI assistant',
          percentage: 0
        });
      } else {
        throw new Error(data.message || 'Failed to start processing');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);

      // Fallback to regular endpoint
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await fetch(`${apiUrl}/api/agent/ask`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            question: inputValue,
            sessionId: sessionId,
            userLanguage: language
          })
        });

        const data = await response.json();

        if (data.success) {
          // /api/agent/ask answers with a flat body: { success, message,
          // suggestions, visualizations }. The data.* forms are tolerated in
          // case the envelope changes again.
          setMessages(prev => [...prev, {
            type: 'ai',
            content: data.message || data.data?.message || data.data?.response || 'I received your message.',
            suggestions: data.suggestions || data.data?.suggestions || [],
            visualizations: extractVisualizations(data),
            timestamp: new Date()
          }]);
        } else {
          throw new Error(data.message);
        }
      } catch (fallbackError) {
        setMessages(prev => [...prev, {
          type: 'ai',
          content: "Sorry, I'm having trouble connecting. Please try again.",
          timestamp: new Date()
        }]);
      }
    }
  }, [isLoading, sessionId]);

  // Handle onboarding
  const handleStartAssessment = async (originalQuestion) => {
    const context = { originalQuestion, source: 'ai_assistant' };
    try {
      const result = await startOnboardingFromChat(context);
      if (result.success) {
        navigate('/onboarding', {
          state: {
            context: context,
            source: 'chat'
          }
        });
        setShowAssessmentPrompt(false);
      }
    } catch (error) {
      console.error('Failed to start onboarding:', error);
    }
  };


  return (
    <div className="chat-interface-root" style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #0a0d14 0%, #161c2c 100%)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Header */}
      <GideonHeader
        showMenuButton={showMenuButton}
        onToggleSidebar={onToggleSidebar}
        conversationCount={conversations.length}
        onShowConversations={onToggleSidebar}
        onNewConversation={onNewConversation}
      />

      {/* Visual Separator */}
      <div style={{
        height: '1px',
        background: 'rgba(255, 255, 255, 0.1)'
      }} />

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
          background: 'transparent'
        }}
      >
        <div style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            maxWidth: '800px',
            width: '100%',
            margin: '0 auto',
            flex: 1,
            paddingTop: '16px',
            paddingBottom: '16px',
            paddingLeft: '16px',
            paddingRight: '16px'
          }}>
            {/* Messages */}
            {messages.map((message, index) => {
              const isLastAiMessage = message.type === 'ai' && index === messages.length - 1;
              return (
                <div
                  key={message.timestamp?.getTime() || index}
                  ref={isLastAiMessage ? lastAiMessageRef : null}
                >
                  <ChatMessage message={message} userName={user?.firstName || user?.name || user?.email} />
                </div>
              );
            })}

            {/* Progress Indicator */}
            {isLoading && currentProgress && (
              <div style={{
                margin: '20px 0',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
              }}>
                {isMobile ? (
                  <CompactProgressIndicator
                    step={currentProgress.step}
                    percentage={currentProgress.percentage}
                  />
                ) : (
                  <ProgressIndicator
                    step={currentProgress.step}
                    message={currentProgress.message}
                    percentage={currentProgress.percentage}
                  />
                )}
              </div>
            )}

            {/* Typing indicator */}
            {isLoading && !currentProgress && (
              <TypingIndicator text="Thinking..." />
            )}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button
            onClick={() => scrollToBottom()}
            aria-label="Scroll to bottom"
            style={{
              position: 'absolute',
              bottom: '16px',
              right: '24px',
              zIndex: 10,
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #00e1c4, #5fffe6)',
              border: 'none',
              borderRadius: '50%',
              color: '#0a0d14',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(0, 225, 196, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            ↓
          </button>
        )}
      </div>

      {/* Input Area */}
      <ChatInput
        onSendMessage={handleSendMessage}
        disabled={isLoading}
      />
    </div>
  );
};

export default ChatInterface;
